<template>
  <TransitionRoot as="template" :show="localeIsModalOpen" :class="[zIndex]">
    <Dialog
      as="div"
      class="relative"
      @close="clickOutsideClose ? close() : null"
      @keydown.esc="handleEscapeKeydown"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-blue-100/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex h-dvh items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="flex transform flex-col px-4 pt-5 pb-4 transition-all"
              :class="{
                'bg-white text-left shadow-xl dark:bg-slate-700': !translucent,
                'fixed inset-0': fullScreen,
                // Full viewport below md: keep the footer above the iOS home bar
                'fixed inset-0 max-md:pb-[calc(env(safe-area-inset-bottom)+1rem)] md:relative md:rounded-lg':
                  !fullScreen,
                'md:max-h-[95vh]': !fullScreen && size === 'huge',
                'md:max-h-[80vh]': !fullScreen && size !== 'huge',
                'md:w-10/12': !fullScreen && large,
                'sm:p-6 md:w-[97%]': !fullScreen && !large && size === 'huge',
                'sm:p-6 md:w-10/12':
                  !fullScreen && !large && size === 'extra-large',
                'sm:p-6 md:w-8/12': !fullScreen && !large && size === 'large',
                'sm:p-6 md:w-1/2': !fullScreen && !large && size === 'medium',
                'sm:p-4 md:w-full md:max-w-lg':
                  !fullScreen &&
                  !large &&
                  !['huge', 'extra-large', 'large', 'medium'].includes(size)
              }"
            >
              <div class="absolute top-0 right-0 block pt-4 pr-4">
                <div class="flex flex-row gap-x-3">
                  <slot name="header" />
                  <button
                    v-if="allowFullScreen"
                    type="button"
                    :class="[
                      !translucent
                        ? 'bg-white text-slate-300 hover:text-gray-500'
                        : 'text-gray-700 hover:text-gray-900',
                      'hidden rounded-md focus:outline-none md:block dark:bg-slate-700'
                    ]"
                    @click="fullScreen = !fullScreen"
                  >
                    <FontAwesomeIcon
                      :icon="[
                        'fal',
                        fullScreen ? 'arrows-minimize' : 'arrows-maximize'
                      ]"
                    />
                  </button>
                  <button
                    type="button"
                    :class="[
                      !translucent
                        ? 'bg-white text-slate-300 hover:text-gray-500'
                        : 'text-gray-700 hover:text-gray-900',
                      'rounded-md focus:outline-none dark:bg-slate-700'
                    ]"
                    @click="close"
                  >
                    <FontAwesomeIcon :icon="['fal', 'times']" size="xl" />
                  </button>
                </div>
              </div>
              <div
                :class="[
                  !translucent ? 'sm:flex' : '',
                  'flex min-h-0 flex-1 flex-col sm:items-start'
                ]"
              >
                <div
                  v-if="icon"
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center sm:mx-0 sm:h-10 sm:w-10"
                >
                  <FontAwesomeIcon :icon="['fal', icon]" size="xl" />
                </div>
                <div
                  :class="[
                    !translucent ? '' : 'justify-center',
                    'flex min-h-0 w-full flex-1 flex-col gap-3'
                  ]"
                >
                  <div class="shrink-0 empty:hidden dark:text-white">
                    <DialogTitle
                      v-if="title && title.length"
                      as="h3"
                      class="text-2xl leading-tight font-bold text-gray-900 capitalize-first dark:text-white"
                    >
                      {{ title }}
                    </DialogTitle>
                    <h4
                      v-if="$slots.subtitle"
                      class="text-lg leading-tight font-semibold text-gray-900 capitalize-first dark:text-white"
                    >
                      <slot name="subtitle" />
                    </h4>
                  </div>
                  <div
                    class="min-h-0 w-full flex-1 overflow-x-hidden dark:text-white"
                    :class="{
                      flex: !translucent,
                      'overflow-y-auto': !lockScrollY,
                      'overflow-y-hidden': lockScrollY
                    }"
                  >
                    <slot name="body" />
                  </div>
                </div>
              </div>
              <div
                v-if="$slots.footer"
                class="mt-5 flex shrink-0 flex-col-reverse gap-2 sm:mt-4 sm:flex-row-reverse dark:text-white"
              >
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'BaseCenteredModal',
  components: {
    FontAwesomeIcon,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    icon: {
      type: String,
      required: false,
      default: null
    },
    large: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: null
    },
    allowFullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    translucent: {
      type: Boolean,
      required: false,
      default: false
    },
    clickOutsideClose: {
      type: Boolean,
      required: false,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      required: false,
      default: null
    },
    lockScrollY: {
      type: Boolean,
      required: false,
      default: false
    },
    zIndex: {
      type: String,
      required: false,
      default: 'z-100'
    }
  },
  emits: ['update:isModalOpen', 'close'],
  data() {
    return {
      fullScreen: false
    }
  },
  computed: {
    localeIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:isModalOpen', value)
      }
    },
    effectiveCloseOnEscape() {
      // Par défaut, Échap suit la même règle que le clic extérieur (pas de
      // fermeture accidentelle quand clickOutsideClose est désactivé). Un
      // appelant peut explicitement forcer `close-on-escape` pour autoriser
      // Échap même si le clic extérieur reste bloqué.
      return this.closeOnEscape ?? this.clickOutsideClose
    }
  },
  methods: {
    handleEscapeKeydown(event) {
      // HeadlessUI pose son propre listener Echap sur `window` (dialog.js)
      // et ignore la touche si `event.defaultPrevented` est déjà vrai. Sans
      // ce preventDefault, les deux handlers se déclenchent l'un après
      // l'autre pour la même pression : close()/emit('close') appelé deux
      // fois. En l'appelant systématiquement ici, ce handler devient seul
      // décisionnaire pour Echap — y compris pour le bloquer complètement
      // quand `effectiveCloseOnEscape` est faux (jusqu'ici HeadlessUI
      // refermait quand même via son propre mécanisme dans ce cas).
      event.preventDefault()
      if (this.effectiveCloseOnEscape) {
        this.close()
      }
    },
    close() {
      this.localeIsModalOpen = false
      this.$emit('close')
    }
  }
}
</script>
